import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ReviewsDashboardCard } from "@/components/reviews/ReviewsDashboardCard";
import { OverduePaymentsCard } from "@/components/OverduePaymentsCard";
import { OverdueDeliveriesCard } from "@/components/OverdueDeliveriesCard";
import { ShopFloorProductionPanel } from "@/components/production/ShopFloorProductionPanel";
import { DashboardFlightControl } from "@/components/dashboard/DashboardFlightControl";
import { 
  Users, 
  DollarSign, 
  CheckCircle,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  FileText,
  Calendar,
  UsersRound,
  UserPlus,
  ClipboardList,
  User,
  ShoppingCart,
  Settings,
  BarChart3,
  Truck,
  FolderOpen,
  Zap,
  LayoutGrid,
  Briefcase,
  Store,
  Trophy,
  Factory,
  Cpu
} from "lucide-react";

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Contact Manager modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);
  const [loadingManager, setLoadingManager] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent-activities?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentActivities(activitiesRes.data);
    } catch (error) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const openContactManager = async () => {
    setShowContactModal(true);
    setLoadingManager(true);
    try {
      const response = await axios.get(`${API}/team/my-manager`);
      setManagerInfo(response.data);
    } catch (error) {
      setManagerInfo(null);
    } finally {
      setLoadingManager(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4 text-blue-500" />;
      case 'sms': return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'call': return <Phone className="w-4 h-4 text-purple-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if user is Owner or Project Manager (has full access)
  const isOwner = user?.role === "owner";
  const isOfficeAdmin = user?.role === "office_admin";
  const isPM = user?.role === "project_manager";
  const canCreate = isOwner || isOfficeAdmin || isPM;
  const isManager = isOwner || isOfficeAdmin || isPM;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {isManager && <DashboardFlightControl />}

      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 mt-1">
            {!isManager
              ? "Here's your schedule and tasks for today."
              : "Here's what's happening with your business today."
            }
          </p>
        </div>
        {isManager && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                toast.loading("Sending daily digest...");
                await axios.post(`${API}/dashboard/send-daily-digest`);
                toast.dismiss();
                toast.success("Daily digest sent to your email!");
              } catch (err) {
                toast.dismiss();
                toast.error("Failed to send digest");
              }
            }}
            data-testid="send-digest-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send Daily Digest
          </Button>
        )}
      </div>

      {/* Quick Access Grid - role-based */}
      <div>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-testid="quick-access-heading">Quick Access</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4" data-testid="quick-access-grid">
          {["owner", "project_manager", "office_admin", "employee"].includes(user?.role) && <QuickCard icon={Cpu} label="CNC Router Station" color="cyan" onClick={() => navigate("/cnc-router-station")} testId="qa-cnc" />}
          <QuickCard icon={Factory} label="Shop Floor Production" color="emerald" onClick={() => document.getElementById("shop-floor-production")?.scrollIntoView({ behavior: "smooth" })} testId="qa-shop-floor-production" />
          {(isOwner || isOfficeAdmin) && <QuickCard icon={Store} label="Suppliers" color="rose" onClick={() => navigate("/suppliers")} testId="qa-suppliers" />}
          {isOwner && <QuickCard icon={UserPlus} label="Leads" count={stats?.total_leads} color="indigo" onClick={() => navigate("/leads")} testId="qa-leads" />}
          {(isOwner || isPM || isOfficeAdmin) && <QuickCard icon={Briefcase} label="Customers" color="sky" onClick={() => navigate("/customers")} testId="qa-customers" />}
          {(isOwner || isPM || isOfficeAdmin) && <QuickCard icon={ShoppingCart} label="Orders" count={stats?.pending_orders} badge="pending" color="emerald" onClick={() => navigate("/orders")} testId="qa-orders" />}
          {(isOwner || isPM || isOfficeAdmin) && <QuickCard icon={FileText} label="Estimates" count={stats?.total_estimates} color="green" onClick={() => navigate("/estimates")} testId="qa-estimates" />}
          <QuickCard icon={Clock} label="Timecards" color="cyan" onClick={() => navigate("/timecard")} testId="qa-timecard" />
          {(isOwner || isOfficeAdmin) && <QuickCard icon={UsersRound} label="Team" count={stats?.team_count} color="orange" onClick={() => navigate("/team")} testId="qa-team" />}
          <QuickCard icon={Calendar} label="Schedule" count={stats?.upcoming_events} badge="upcoming" color="purple" onClick={() => navigate("/calendar")} testId="qa-schedule" />
          <QuickCard icon={ClipboardList} label="Tasks" count={stats?.pending_tasks} badge="pending" color="yellow" onClick={() => navigate("/tasks")} testId="qa-tasks" />
          {(isOwner || isPM || isOfficeAdmin) && <QuickCard icon={FileText} label="Forms" color="teal" onClick={() => navigate("/forms")} testId="qa-forms" />}
          {isOwner && <QuickCard icon={BarChart3} label="Analytics" color="violet" onClick={() => navigate("/analytics")} testId="qa-analytics" />}
          <QuickCard icon={Settings} label="Settings" color="slate" onClick={() => navigate("/settings")} testId="qa-settings" />
          {!isOwner && !isOfficeAdmin && !isPM && <QuickCard icon={User} label="Contact Manager" color="blue" onClick={openContactManager} testId="qa-contact" />}
        </div>
      </div>

      <ShopFloorProductionPanel />

      {isManager && <OverduePaymentsCard />}
      {isManager && <OverdueDeliveriesCard />}
      {isManager && <ReviewsDashboardCard />}

      {/* Recent Activity - Only show for managers */}
      {isManager && (
      <Card data-testid="recent-activities">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivities.length > 0 ? (
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <div key={activity.activity_id || index} className="activity-item">
                  <div className="activity-icon bg-slate-100">
                    {getActivityIcon(activity.activity_type)}
                  </div>
                  <div className="activity-content">
                    <p className="text-sm text-slate-900">
                      <span className="font-medium">
                        {activity.lead?.first_name} {activity.lead?.last_name}
                      </span>
                      {" - "}
                      {activity.activity_type}
                    </p>
                    <p className="text-sm text-slate-500 truncate">{activity.content}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(activity.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state py-8">
              <Clock className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500">No recent activity</p>
              <p className="text-sm text-slate-400">Start adding leads to see activity here</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Contact Manager Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Your Manager
            </DialogTitle>
          </DialogHeader>
          
          {loadingManager ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : managerInfo ? (
            <div className="py-4">
              {/* Manager Card */}
              <div className="flex flex-col items-center text-center mb-6">
                <Avatar className="w-20 h-20 mb-3">
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xl font-semibold">
                    {getInitials(managerInfo.name)}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-semibold text-slate-900">{managerInfo.name}</h3>
                <p className="text-sm text-blue-600 font-medium">{managerInfo.role}</p>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-3">
                {managerInfo.phone && (
                  <a 
                    href={`tel:${managerInfo.phone}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-slate-500">Phone</p>
                      <p className="font-medium text-slate-900">{managerInfo.phone}</p>
                    </div>
                  </a>
                )}
                
                {managerInfo.email && (
                  <a 
                    href={`mailto:${managerInfo.email}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{managerInfo.email}</p>
                    </div>
                  </a>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-2 mt-6">
                {managerInfo.phone && (
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => window.location.href = `tel:${managerInfo.phone}`}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call
                  </Button>
                )}
                {managerInfo.email && (
                  <Button 
                    className="flex-1"
                    onClick={() => window.location.href = `mailto:${managerInfo.email}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No manager information available</p>
              <p className="text-sm text-slate-400 mt-1">Please contact support</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const colorMap = {
  indigo: { bg: "bg-indigo-100", hover: "hover:border-indigo-300", text: "text-indigo-600", hoverBg: "group-hover:bg-indigo-200" },
  emerald: { bg: "bg-emerald-100", hover: "hover:border-emerald-300", text: "text-emerald-600", hoverBg: "group-hover:bg-emerald-200" },
  green: { bg: "bg-green-100", hover: "hover:border-green-300", text: "text-green-600", hoverBg: "group-hover:bg-green-200" },
  sky: { bg: "bg-sky-100", hover: "hover:border-sky-300", text: "text-sky-600", hoverBg: "group-hover:bg-sky-200" },
  purple: { bg: "bg-purple-100", hover: "hover:border-purple-300", text: "text-purple-600", hoverBg: "group-hover:bg-purple-200" },
  yellow: { bg: "bg-yellow-100", hover: "hover:border-yellow-300", text: "text-yellow-600", hoverBg: "group-hover:bg-yellow-200" },
  orange: { bg: "bg-orange-100", hover: "hover:border-orange-300", text: "text-orange-600", hoverBg: "group-hover:bg-orange-200" },
  rose: { bg: "bg-rose-100", hover: "hover:border-rose-300", text: "text-rose-600", hoverBg: "group-hover:bg-rose-200" },
  violet: { bg: "bg-violet-100", hover: "hover:border-violet-300", text: "text-violet-600", hoverBg: "group-hover:bg-violet-200" },
  amber: { bg: "bg-amber-100", hover: "hover:border-amber-300", text: "text-amber-600", hoverBg: "group-hover:bg-amber-200" },
  teal: { bg: "bg-teal-100", hover: "hover:border-teal-300", text: "text-teal-600", hoverBg: "group-hover:bg-teal-200" },
  cyan: { bg: "bg-cyan-100", hover: "hover:border-cyan-300", text: "text-cyan-600", hoverBg: "group-hover:bg-cyan-200" },
  blue: { bg: "bg-blue-100", hover: "hover:border-blue-300", text: "text-blue-600", hoverBg: "group-hover:bg-blue-200" },
  slate: { bg: "bg-slate-100", hover: "hover:border-slate-300", text: "text-slate-600", hoverBg: "group-hover:bg-slate-200" },
};

function QuickCard({ icon: Icon, label, count, badge, color, onClick, testId }) {
  const c = colorMap[color] || colorMap.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="min-w-0 text-left"
    >
      <Card className={`group h-full min-h-[96px] cursor-pointer transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md ${c.hover}`}>
        <CardContent className="flex h-full items-center px-3 py-4 sm:px-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${c.bg} ${c.hoverBg} transition-colors`}>
              <Icon className={`h-5 w-5 ${c.text}`} />
            </div>
            <div className="min-w-0">
              <p className="break-words text-sm font-medium leading-5 text-slate-700" data-testid={`${testId}-label`}>{label}</p>
              {count != null && (
                <p className={`text-lg font-bold ${c.text} leading-tight`} data-testid={`${testId}-count`}>
                  {count}{badge && <span className="ml-1 text-xs font-normal text-slate-400">{badge}</span>}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
}
